"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ToolAnnotations } from "../model/component-metadata";
import { useTamboInteractable } from "../providers/tambo-interactable-provider";
import { SupportedSchema } from "../schema";
import { TamboMessageProvider } from "../v1/hooks/use-tambo-current-message";
import type { TamboThreadMessage } from "../v1/types/message";
import { ComponentContentProvider } from "../v1/utils/component-renderer";

export interface InteractableConfig<
  Props = Record<string, unknown>,
  State = Record<string, unknown>,
> {
  /**
   * The name of the component, used for identification in Tambo.
   */
  componentName: string;
  /**
   * A brief description of the component's purpose and functionality. LLM will
   * use this to understand how to interact with it.
   */
  description: string;
  /**
   * Optional schema for component props. If provided, prop updates will be
   * validated against this schema.
   */
  propsSchema?: SupportedSchema<Props>;
  /**
   * Optional schema for component state. If provided, state updates will be
   * validated against this schema.
   */
  stateSchema?: SupportedSchema<State>;
  /**
   * Optional annotations for the interactable component's auto-registered tools
   * (props update and state update). By default, `tamboStreamableHint` is `true`
   * so props/state updates stream in real-time. Set `{ tamboStreamableHint: false }`
   * to disable streaming for this component's tools.
   */
  annotations?: ToolAnnotations;
}

/**
 * Props injected by withTamboInteractable HOC. These can be passed to the wrapped
 * component to customize interactable behavior.
 */
export interface WithTamboInteractableProps {
  /**
   * Optional ID to use for this interactable component instance.
   * If not provided, a unique ID will be generated automatically.
   */
  interactableId?: string;
  /**
   * Callback fired when the component has been registered as interactable.
   * @param id - The assigned interactable component ID
   */
  onInteractableReady?: (id: string) => void;
  /**
   * Callback fired when the component's serializable props are updated by Tambo
   * through a tool call. Note: Only serializable props are tracked.
   * @param newProps - The updated serializable props
   */
  onPropsUpdate?: (newProps: Record<string, unknown>) => void;
}

/**
 * Higher-Order Component that makes any component interactable by tambo.
 * @param WrappedComponent - The component to make interactable
 * @param config - Configuration for the interactable component
 * @returns A new component that is automatically registered as interactable
 * @example
 * ```tsx
 * const MyNote: React.FC<{ title: string; content: string }> = ({ title, content }) => {
 *   const [isPinned, setIsPinned] = useTamboComponentState("isPinned", false);
 *   return (
 *     <div style={{ border: isPinned ? "2px solid gold" : "1px solid gray", order: isPinned ? -1 : 0 }}>
 *       <h2>{title}</h2>
 *       <p>{content}</p>
 *     </div>
 *   );
 * };
 *
 * const MyInteractableNote = withTamboInteractable(MyNote, {
 *   componentName: "MyNote",
 *   description: "A note component",
 *   propsSchema: z.object({
 *     title: z.string(),
 *     content: z.string(),
 *   }),
 *  stateSchema: z.object({
 *    isPinned: z.boolean(),
 *  }),
 * });
 *
 * // Usage
 * <MyInteractableNote title="My Note" content="This is my note" />
 * ```
 */
export function withTamboInteractable<ComponentProps extends object>(
  WrappedComponent: React.ComponentType<ComponentProps>,
  config: InteractableConfig,
) {
  const displayName =
    WrappedComponent.displayName ?? WrappedComponent.name ?? "Component";

  const TamboInteractableWrapper: React.FC<
    ComponentProps & WithTamboInteractableProps
  > = (props) => {
    const {
      addInteractableComponent,
      removeInteractableComponent,
      updateInteractableComponentProps,
      getInteractableComponent,
    } = useTamboInteractable();

    const [interactableId, setInteractableId] = useState<string | null>(null);
    const isInitialized = useRef(false);
    const lastSerializedProps = useRef<Record<string, unknown>>({});

    // Extract interactable-specific props from component props
    const {
      interactableId: _providedId, // Reserved for future use
      onInteractableReady,
      onPropsUpdate,
      ...componentProps
    } = props;

    // Get the current interactable component to track prop updates
    const currentInteractable = interactableId
      ? getInteractableComponent(interactableId)
      : null;

    // Use the props from the interactable component if available, otherwise use the passed props
    // We need to be careful not to create a loop, so we only use stored props if they're different from passed props
    const effectiveProps = currentInteractable?.props ?? componentProps;

    // Memoize the registration function
    const registerComponent = useCallback(() => {
      if (!isInitialized.current) {
        const id = addInteractableComponent({
          name: config.componentName,
          description: config.description,
          component: WrappedComponent,
          props: componentProps,
          propsSchema: config.propsSchema,
          stateSchema: config.stateSchema,
          annotations: config.annotations,
        });

        setInteractableId(id);
        onInteractableReady?.(id);
        isInitialized.current = true;
      }
    }, [addInteractableComponent, componentProps, onInteractableReady]);

    // Register the component as interactable on mount
    useEffect(() => {
      registerComponent();
    }, [registerComponent]);

    // Clean up on unmount only, keyed on stable values
    useEffect(() => {
      return () => {
        if (isInitialized.current && interactableId) {
          removeInteractableComponent(interactableId);
          isInitialized.current = false;
        }
      };
    }, [interactableId, removeInteractableComponent]);

    // Update the interactable component when props change from parent
    useEffect(() => {
      if (interactableId && isInitialized.current) {
        // Only update if the props are different from what we last sent
        const lastPropsString = JSON.stringify(lastSerializedProps.current);
        const currentPropsString = JSON.stringify(componentProps);

        if (lastPropsString !== currentPropsString) {
          updateInteractableComponentProps(interactableId, componentProps);
          onPropsUpdate?.(componentProps);
          lastSerializedProps.current = componentProps;
        }
      }
    }, [
      interactableId,
      componentProps,
      updateInteractableComponentProps,
      onPropsUpdate,
    ]);

    // If the interactable ID is not yet set, render the component without provider
    if (!interactableId) {
      return <WrappedComponent {...(effectiveProps as ComponentProps)} />;
    }

    // Create a minimal message with interactable metadata
    // This allows useTamboCurrentComponent to work with standalone interactable components
    const minimalMessage: TamboThreadMessage = {
      id: interactableId,
      role: "assistant" as const,
      content: [
        {
          type: "component" as const,
          id: interactableId,
          name: config.componentName,
          props: effectiveProps,
          state: {},
        },
      ],
      createdAt: new Date().toISOString(),
    };

    // Wrap with TamboMessageProvider and ComponentContentProvider including interactable metadata
    return (
      <TamboMessageProvider
        message={minimalMessage}
        interactableMetadata={{
          id: interactableId,
          componentName: config.componentName,
          description: config.description,
        }}
      >
        <ComponentContentProvider
          componentId={interactableId}
          threadId=""
          messageId=""
          componentName={config.componentName}
        >
          <WrappedComponent {...(effectiveProps as ComponentProps)} />
        </ComponentContentProvider>
      </TamboMessageProvider>
    );
  };

  TamboInteractableWrapper.displayName = `withTamboInteractable(${displayName})`;

  return TamboInteractableWrapper;
}
